<script setup lang="ts">
import type { Component } from 'vue';
import 'swiper/css/navigation';
import { useBanners } from '~/composables/apostatudo/use-banners';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';

const { isMobile } = useLayout();
const { getLoadingStrategy } = useImageLoading();
const { getResponsiveBannerUrlWithWidth } = useResponsiveBannerUrl();
const { homeBanners, fetchBanners, hasHomeBanners } = useBanners();

const bannerContainer = ref<HTMLDivElement | null>(null);

// Intersection Observer para lazy loading dos banners
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (bannerContainer.value && !hasHomeBanners.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchBanners();
            observer?.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      },
    );

    observer.observe(bannerContainer.value);
  }
});

onUnmounted(() => {
  observer?.disconnect();
});

// Lazy load Swiper
const Swiper = shallowRef<Component | null>(null);
const SwiperSlide = shallowRef<Component | null>(null);
const Navigation = shallowRef<any>(null);
const swiperLoaded = ref(false);

onBeforeMount(async () => {
  const [swiperVue, swiperModules, swiperElement] = await Promise.all([
    import('swiper/vue'),
    import('swiper/modules'),
    import('swiper/element/bundle'),
  ]);

  Swiper.value = swiperVue.Swiper;
  SwiperSlide.value = swiperVue.SwiperSlide;
  Navigation.value = swiperModules.Navigation;
  swiperElement.register();
  swiperLoaded.value = true;
});

type Props = {
  isAboveFold?: boolean;
};

const { isAboveFold = false } = defineProps<Props>();

const itemsAboveFold = computed(() => (isAboveFold ? 2 : 0));

const showInformationSection = computed(() => {
  return homeBanners.value && homeBanners.value.length > 0;
});

const handleActionURL = (linkUrl: string | null) => {
  if (!linkUrl) return '/';

  if (linkUrl.startsWith('/')) {
    return linkUrl;
  }

  if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
    try {
      const url = new URL(linkUrl);

      if (url.hostname.includes('apostatudo.bet')) {
        return url.pathname + url.search + url.hash;
      }

      return linkUrl;
    } catch {
      return linkUrl;
    }
  }

  return linkUrl;
};

const isExternalLink = (linkUrl: string | null) => {
  if (!linkUrl) return false;

  if (linkUrl.startsWith('/')) return false;

  if (linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
    try {
      const url = new URL(linkUrl);

      return !url.hostname.includes('apostatudo.bet');
    } catch {
      return true;
    }
  }

  return false;
};

// Loop sempre habilitado quando tiver slides suficientes
const shouldEnableLoop = computed(() => {
  return homeBanners.value.length >= 3;
});
</script>

<template>
  <!-- Skeleton/Loading state -->
  <div
    v-if="!showInformationSection && isMobile"
    ref="bannerContainer"
    class="banner-carousel-wrapper"
    style="min-height: 122px"
  >
    <div class="flex gap-[10px] justify-center">
      <BaseCommonSkeleton
        class="flex-shrink-0 rounded-xl"
        style="width: 80%; height: 122px; aspect-ratio: 282/122.46"
      />
    </div>
  </div>

  <div
    v-if="showInformationSection"
    ref="bannerContainer"
    class="banner-carousel-wrapper"
  >
    <div
      v-if="swiperLoaded"
      class="banner-slider"
    >
      <component
        :is="Swiper"
        :autoplay="{
          delay: 6000,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
        }"
        :centered-slides="true"
        class="infoSwiper h-full"
        create-elements
        :loop="shouldEnableLoop"
        :modules="[Navigation]"
        :navigation="{
          prevEl: '.info-swiper-prev',
          nextEl: '.info-swiper-next',
        }"
        preload-images="false"
        :slides-per-group="1"
        :slides-per-view="1.2"
        :space-between="4"
        :speed="500"
      >
        <component
          :is="SwiperSlide"
          v-for="(banner, index) in homeBanners"
          :key="banner.id"
          class="h-full"
        >
          <NuxtLink
            v-if="banner"
            class="block h-full"
            :external="isExternalLink(banner.link_url)"
            no-prefetch
            :target="isExternalLink(banner.link_url) ? '_blank' : '_self'"
            :to="handleActionURL(banner.link_url)"
          >
            <img
              :alt="banner.filename || `Banner ${index}`"
              class="rounded-xl object-cover w-full h-full"
              height="122"
              :loading="getLoadingStrategy(index, itemsAboveFold)"
              :src="getResponsiveBannerUrlWithWidth(banner.url)"
              style="aspect-ratio: 282/122.46"
              width="282"
            />
          </NuxtLink>
        </component>
      </component>

      <div class="info-swiper-prev navigation-button navigation-button-prev">
        <NuxtIcon
          :filled="true"
          name="arrows/chevron_left"
        />
      </div>
      <div class="info-swiper-next navigation-button navigation-button-next">
        <NuxtIcon
          :filled="true"
          name="arrows/chevron_right"
        />
      </div>
    </div>
  </div>

  <!-- Placeholder invisível para Intersection Observer quando não há banners -->
  <div
    v-if="!showInformationSection && !isMobile"
    ref="bannerContainer"
    aria-hidden="true"
    class="invisible h-1"
  ></div>
</template>

<style scoped lang="scss">
.banner-carousel-wrapper {
  @apply select-none overflow-hidden mb-4 sm:mb-5 md:mb-8;
  min-height: 122.46px;
  margin-left: calc(-1 * var(--container-padding, 16px));
  margin-right: calc(-1 * var(--container-padding, 16px));
}

.banner-slider {
  @apply relative;
  width: 100%;
}

// Swiper customizado
.infoSwiper {
  @apply w-full;

  :deep(.swiper-wrapper) {
    align-items: center;
  }

  :deep(.swiper-button-next),
  :deep(.swiper-button-prev) {
    display: none;
  }

  :deep(.swiper-slide) {
    opacity: 0.4;
    z-index: 1;
    transition: all 0.4s ease;
    border-radius: 0.75rem;
    overflow: hidden;
    transform: scaleY(0.82);
  }

  :deep(.swiper-slide-active) {
    opacity: 1;
    z-index: 2;
    transform: scaleY(1);
  }
}

// Botões de navegação overlay - mesmo estilo do SliderApostatudo
.navigation-button {
  @apply absolute z-10 flex items-center justify-center
         rounded-[5px] shadow-lg text-texts font-extrabold cursor-pointer;

  width: 25px;
  height: 25px;
  top: 50%;
  transform: translateY(-50%);
  background: var(
    --ATNB---ORANGE,
    radial-gradient(168.3% 138% at 36.76% -16.67%, #ff704a 0%, #ff3400 100%)
  );

  &.navigation-button-prev {
    left: 5%;
  }

  &.navigation-button-next {
    right: 5%;
  }

  @media (min-width: 640px) {
    width: 32px;
    height: 32px;
  }
}
</style>
